import { useQuery } from 'react-query';
import { makeStyles } from '@material-ui/core/styles';
import exerciseQueuesApi, { exerciseQueuesApiKeys } from 'api/rest/exerciseQueues/exerciseQueues';
import { Subject } from 'types/Subject';
import PageLoader from 'components/PageLoader/PageLoader';
import { FC, useMemo } from 'react';
import OnGoExercise from './Exercise/OnGoExercise';
import OnGoInitialize from './Initialize/OnGoInitialize';
import OnGoSummary from './Summary/OnGoSummary';
import { resolveOnGoScreen } from './resolveOnGoScreen';
import subjectsApi from 'api/rest/subjects/subjects';
import QUERY from 'constants/queryKeys';

const useStyles = makeStyles({
  root: {
    flex: 1,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  loaderFill: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
});

type OnGoContentProps = {
  subject: Subject;
};

const OnGoRouter: FC<OnGoContentProps> = ({ subject }) => {
  const classes = useStyles();

  const {
    data: activeQueueData,
    isLoading: isActiveQueueLoading,
    isFetching: isActiveQueueFetching,
    isError: isActiveQueueError,
  } = useQuery(exerciseQueuesApiKeys.active(subject.pk), exerciseQueuesApi.getActive(subject.pk), {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { data: sectionsData } = useQuery(
    [QUERY.GET_SINGLE_SUBJECT, subject.pk],
    subjectsApi.getSubject(subject.pk),
    {
      enabled: !!subject.pk,
      staleTime: 1000 * 60 * 60,
      select: ({ sections }) => sections,
    },
  );

  const screenType = useMemo(
    () =>
      resolveOnGoScreen({
        activeQueue: activeQueueData,
        sectionsData,
        isActiveQueueLoading,
        isActiveQueueFetching,
        isActiveQueueError,
      }),
    [activeQueueData, sectionsData, isActiveQueueLoading, isActiveQueueFetching, isActiveQueueError],
  );

  let body: JSX.Element | null = null;
  switch (screenType) {
    case 'loading':
      body = (
        <div className={classes.loaderFill}>
          <PageLoader />
        </div>
      );
      break;
    case 'initialize':
      body = <OnGoInitialize sectionsData={sectionsData || []} />;
      break;
    case 'summary':
      body = activeQueueData ? <OnGoSummary queue={activeQueueData} subject={subject} /> : null;
      break;
    case 'exercise':
      body = activeQueueData ? <OnGoExercise queueId={activeQueueData.uuid} subject={subject} /> : null;
      break;
    default:
      body = null;
  }

  return <div className={classes.root}>{body}</div>;
};

export default OnGoRouter;
